/**
 * DronesHub — Main page for live broadcasting via WebRTC.
 * Start a broadcast from your laptop camera/mic and share the unique URL.
 *
 * Two sub-pages accessed via Tabs or URLs:
 * - /drones → This hub page (list active streams + start broadcast)
 * - /drones/broadcast → Full-screen broadcaster with camera controls
 * - /drones/stream/:roomId → Public viewer page (no login required)
 */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/App";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";
import {
  Loader2, Camera, Video, Wifi, WifiOff, Users, Globe,
  Copy, Share2, Plus, Play, StopCircle, Monitor,
} from "lucide-react";

const API = (() => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8001/api";
  return `https://api.${host}/api`;
})();

const RELAY_API = "https://stream-relay-production.up.railway.app";
const WEBSOCKET_URL = "wss://stream-relay-production.up.railway.app/ws/webrtc";

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ── Drones Hub Page ──
export default function DronesHub() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [activeRooms, setActiveRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchStreams(); }, []);

  const fetchStreams = async () => {
    setLoading(true);
    try {
      const [roomsRes, hlsRes, backendRes] = await Promise.all([
        axios.get(RELAY_API + "/api/webrtc/rooms", { timeout: 5000 }),
        axios.get(RELAY_API + "/api/streams", { timeout: 5000 }).catch(() => ({ data: { streams: [] } })),
        axios.get(API + "/broadcast/live", { headers: getHeaders(), timeout: 5000 }).catch(() => ({ data: { broadcasts: [] } })),
      ]);
      const rooms = roomsRes.data.rooms || [];
      const hls = (hlsRes.data.streams || []).map(s => ({
        roomId: s.name,
        name: s.name,
        hasBroadcaster: true,
        viewerCount: 0,
        metadata: { title: "HLS: " + s.name, broadcaster_name: "Broadcaster" },
        isHls: true,
        hlsUrl: s.hlsUrl,
      }));
      const backendBcs = (backendRes.data.broadcasts || []).map(b => ({
        roomId: b.id,
        name: b.id,
        hasBroadcaster: true,
        viewerCount: b.viewers || 0,
        isBroadcast: true,
        metadata: { title: b.title || "Broadcast " + b.id.slice(0, 8), broadcaster_name: b.broadcaster || "Anonymous" },
      }));
      setActiveRooms([...rooms, ...hls, ...backendBcs]);
    } catch { setActiveRooms([]); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-app text-app">
      <TopNav />
      <div className="max-w-7xl mx-auto px-3 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Live Broadcasts</h1>
              <p className="text-xs text-app-secondary">Start or watch live streams from your browser</p>
            </div>
          </div>
          {auth.user && (
            <Button onClick={() => navigate("/drones/broadcast")}
              className="bg-red-600 hover:bg-red-500 text-white text-xs px-3 py-1.5 h-auto rounded-lg">
              <Plus className="w-3.5 h-3.5 mr-1" /> Start Broadcast
            </Button>
          )}
        </div>

        {/* Active Broadcasts */}
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-app-secondary" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {auth.user && (
              <Card className="bg-app-card border border-dashed border-app-secondary/40 p-6 flex flex-col items-center justify-center min-h-[200px] cursor-pointer hover:border-cyan-500/50 transition-colors"
                onClick={() => navigate("/drones/broadcast")}>
                <div className="w-12 h-12 rounded-full bg-red-600/20 flex items-center justify-center mb-3">
                  <Video className="w-6 h-6 text-red-400" />
                </div>
                <p className="text-sm font-medium text-app">Go Live</p>
                <p className="text-[10px] text-app-secondary mt-1">Start a broadcast from your camera</p>
              </Card>
            )}

            {activeRooms.length === 0 && (
              <Card className="bg-app-card border-app-secondary/20 p-6 flex flex-col items-center justify-center min-h-[200px]">
                <WifiOff className="w-8 h-8 text-app-secondary mb-2" />
                <p className="text-sm text-app-secondary">No active broadcasts</p>
                <p className="text-[10px] text-app-secondary mt-1">Start one or wait for someone to go live</p>
              </Card>
            )}

            {activeRooms.map((room) => (
              <Card key={room.roomId} className="bg-app-card border-app-secondary/20 overflow-hidden hover:border-cyan-500/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/drones/stream/${room.roomId}`)}>
                <div className="h-32 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center relative">
                  <Camera className="w-10 h-10 text-slate-700" />
                  {room.hasBroadcaster && (
                    <div className="absolute top-2 left-2 flex items-center gap-1 bg-red-600/80 px-2 py-0.5 rounded-full">
                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                      <span className="text-[10px] text-white font-medium">LIVE</span>
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-black/60 px-2 py-0.5 rounded text-[10px] text-slate-300">
                    {room.viewerCount || 0} viewers
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-xs font-medium truncate">{room.metadata?.title || room.name}</p>
                  <p className="text-[10px] text-app-secondary mt-0.5 truncate">{room.metadata?.broadcaster_name || "Anonymous"}</p>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
